// src/features/crypto-info/api.ts
//
// Data fetcher for the Crypto Info deep-dive page. Talks to the FastAPI
// crypto_info router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>)
//     so the endpoint is reached only as an authorized user, and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/crypto-info/*` path here — adding
// `/api/v1` would DOUBLE the prefix and 404.
//
// CRITICAL: the backend serializes with `model_dump(by_alias=False,
// exclude_none=True)` in CryptoInfoService.get_overview — so the JSON keys
// are SNAKE_CASE (changes_percentage, market_cap, how_to_trade), NOT the
// camelCase FMP aliases. And because `exclude_none=True` drops null fields,
// every field here is optional.

import { apiClient } from '../../api/client';

const BASE_URL = '/crypto-info';

export interface CryptoQuote {
    symbol: string;
    name?: string;
    price?: number;
    changes_percentage?: number;
    change?: number;
    day_low?: number;
    day_high?: number;
    market_cap?: number;
    volume?: number;
    previous_close?: number;
}

export interface CryptoProfile {
    symbol: string;
    description?: string;
    how_to_trade?: string;
}

export interface BreakoutAlert {
    signal: string;
    action: string;
    buy_price?: number;
    sell_target?: number;
    stop_loss?: number;
    expected_return_pct?: number;
    holding_period?: string;
    reasoning: string;
    sma_50?: number;
    ema_20?: number;
    rsi_14?: number;
}

export interface CryptoOverview {
    symbol: string;
    quote: CryptoQuote;
    profile: CryptoProfile;
    breakout_alert: BreakoutAlert;
}

// GET /crypto-info/{symbol}/chart — one OHLCV bar per row (raw FMP keys,
// already lowercase: date/open/high/low/close/volume), newest-first.
export interface ChartBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

export const fetchCryptoOverview = async (
    symbol: string,
): Promise<CryptoOverview> => {
    // Resolves to GET /api/v1/crypto-info/{symbol}/overview (prefix from baseURL)
    const { data } = await apiClient.get<CryptoOverview>(
        `${BASE_URL}/${encodeURIComponent(symbol)}/overview`,
    );
    return data;
};

export const fetchCryptoChart = async (
    symbol: string,
    interval = '1hour',
): Promise<ChartBar[]> => {
    const { data } = await apiClient.get<ChartBar[]>(
        `${BASE_URL}/${encodeURIComponent(symbol)}/chart`,
        { params: { interval } },
    );
    return data;
};

// GET /crypto-info/{symbol}/technical-indicators — one OHLCV bar per row PLUS
// the indicator value under a key named after the indicator (sma/ema/wma/
// rsi/adx). FMP keys are already lowercase, so an index signature captures
// whichever indicator column comes back on top of the OHLCV fields.
export interface TechnicalIndicatorBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    // The active indicator arrives under its own key (e.g. `sma`, `rsi`).
    [indicator: string]: number | string;
}

// Valid indicators exposed by the backend proxy (mirrors the FMP
// /stable/technical-indicators/{indicator} slugs).
export type TechnicalIndicator = 'sma' | 'ema' | 'wma' | 'rsi' | 'adx';

export const fetchCryptoTechnicalIndicator = async (
    symbol: string,
    params: {
        indicator: TechnicalIndicator;
        periodLength: number;
        timeframe: string;
        from?: string;
        to?: string;
    },
): Promise<TechnicalIndicatorBar[]> => {
    const { data } = await apiClient.get<TechnicalIndicatorBar[]>(
        `${BASE_URL}/${encodeURIComponent(symbol)}/technical-indicators`,
        {
            // Backend aliases these to FMP's exact param names
            // (indicator / periodLength / timeframe / from / to).
            params: {
                indicator: params.indicator,
                periodLength: params.periodLength,
                timeframe: params.timeframe,
                ...(params.from ? { from: params.from } : {}),
                ...(params.to ? { to: params.to } : {}),
            },
        },
    );
    return data;
};
